import type { Image } from './schema'

export const workPage = {
  eyebrow: 'Work',
  title: 'Real builds you can open.',
  lede: 'Automations, AI agents and integrations I have built for teams — every one running in production.',
}

/* ---------- Picture helpers ---------- */

/** Real pixel sizes of the prepared WebP copies in /images/work (public/). */
const dims: Record<string, [number, number]> = {
  'support-ticketing': [1600, 900],
  'support-ticketing-workflows': [1600, 2218],
  'roofing-voice-ai': [1600, 900],
  'roofing-workflows': [1600, 612],
  'roofing-doc': [1200, 848],
  'rag-production': [1600, 900],
  'rag-production-workflow': [1600, 1017],
  'rag-production-telegram': [1200, 1331],
  'sales-ai-agent': [1600, 1035],
  'ai-assistant': [1600, 1035],
  'rag-chatbot': [1600, 1035],
  'invoice-processing': [1600, 1035],
  'gmail-integration': [1600, 1035],
  'asana-xero': [1600, 1035],
  'ghl-automation': [1365, 633],
  'leads-enrichment': [1600, 1035],
  'leads-action': [1600, 1035],
  'google-maps-scraper': [1600, 1035],
  'content-repurposing': [1600, 1035],
  'social-media': [1600, 1035],
  'youtube-shorts': [1600, 1035],
  'viral-videos': [1600, 1035],
  'support-wf-062351': [1176, 1479],
  'support-wf-062506': [1176, 1479],
  'support-wf-062554': [770, 1920],
  'support-wf-062635': [820, 1920],
  'support-wf-062749': [1282, 1920],
  'support-wf-062826': [706, 1280],
}

const img = (slug: string, alt: string): Image => {
  const [width, height] = dims[slug] ?? [1600, 1035]
  return { src: `/images/work/${slug}.webp`, alt, width, height }
}

/* ---------- Featured project: the Support Ticketing System ---------- */

export const featuredProject = {
  kicker: 'Featured project',
  title: 'Support Ticketing System',
  summary:
    'A complete customer-support pipeline built inside GoHighLevel for AA Health Insurance: every request becomes a tracked ticket, categorized, followed up and resolved — with a reopen window, so nothing gets lost.',
  gallery: [
    img('support-ticketing', 'Support Ticketing System infographic: capture, organize, follow up, improve'),
    img('support-wf-061812', 'GoHighLevel workflow that generates the ticket reference, creates the ticket and routes it by category'),
    img('support-wf-061419', 'GoHighLevel intake workflow: form submitted, existing ticket found, pipeline routing'),
    img('support-email-example', 'Support ticket confirmation email with ticket number, category and status'),
    img('support-ticketing-workflows', 'Full workflow breakdown of the Support Ticketing System'),
  ],
}

/* ---------- Its own section: Ridgeline Roofing Voice AI (one project, three pictures) ---------- */

export const roofingProject = {
  title: 'Ridgeline Roofing Voice AI',
  story: [
    'An end-to-end lead management system built in GoHighLevel for a roofing company. Riley, an AI Voice Receptionist, answers incoming calls, collects name, phone, email, property address, roofing concern, service needed, urgency and preferred inspection time, and answers common questions from a roofing knowledge base.',
    'Every call becomes a CRM opportunity moving through New Lead → Qualified → Follow Up → Inspection Booked → Estimate Sent → Job Completed or Lost, with a workflow for each stage. New leads are tagged, qualified by service area and need, and followed up until they book. Booking stops follow-ups, confirms and notifies the team. Estimates get their own follow-up; completed jobs are marked won with a thank-you and review request; lost leads are logged, cleaned up and can enter a reactivation campaign.',
    'Riley’s prompt enforces complete lead capture — one question at a time, never trapping the caller, and marking anything refused as not provided — then triggers the new-lead workflow and books the inspection during the call.',
    'The result: no missed leads, faster response, consistent follow-up and a cleaner sales process.',
  ],
  lead: img('roofing-voice-ai', 'Voice AI Roofing Automation infographic: lead capture, qualification, booking and follow-up'),
  gallery: [
    img('roofing-workflows', 'The eight GoHighLevel workflows behind the Ridgeline Roofing pipeline, New Lead to Job Completed'),
    img('roofing-doc', 'Riley, the AI receptionist: GoHighLevel voice agent prompt and call actions'),
  ],
}

/* ---------- The builds, grouped the way the Work bento shows them ---------- */

export type Build = { id: string; title: string; summary: string; image?: string; alt?: string; group?: string; kicker?: string; detail?: boolean }

/** Builds that count as projects: `detail` entries are extra pictures of the project before them. */
export const countBuilds = (builds: Build[]) => builds.filter((b) => !b.detail).length

export const aiAutomations: Build[] = [
  {
    id: 'production-rag-chatbot',
    group: 'Production RAG Chatbot',
    kicker: 'RAG chatbot · HVAC demo',
    title: 'Production RAG Chatbot',
    summary:
      'An AI customer-support chatbot on Telegram that answers from the company’s own knowledge base, not general AI knowledge. Built for an HVAC demo (Summit Air): it searches a Supabase vector store, reranks the best matches with Cohere, checks the evidence actually supports the question, then answers. If the information isn’t there, it says so and points the customer to the team instead of making something up. Reusable for roofing, dental, real estate, e-commerce, SaaS and more.',
    image: 'rag-production',
    alt: 'Production RAG Chatbot infographic: n8n, Telegram and Supabase with vector store, memory and reranking',
  },
  {
    id: 'production-rag-workflow',
    group: 'Production RAG Chatbot',
    kicker: 'n8n workflow',
    detail: true,
    title: 'Retrieval, Reranking & Hallucination Protection',
    summary:
      'Telegram trigger → validate request → load chat memory → resolve the follow-up question in context → retrieve knowledge (OpenAI embeddings + Supabase) → Cohere rerank → evaluate evidence safety → PASS generates a grounded answer, otherwise a safe fallback. Every turn is saved to PostgreSQL chat memory and a production log (question, confidence, retrieval score, processing time, session ID). A separate global error workflow captures failures in OpenAI, Supabase, Telegram, Cohere or any node.',
    image: 'rag-production-workflow',
    alt: 'n8n workflow of the Production RAG Chatbot, from Telegram trigger to production log',
  },
  {
    id: 'production-rag-telegram',
    group: 'Production RAG Chatbot',
    kicker: 'Live conversation',
    detail: true,
    title: 'Summit Air Assistant on Telegram',
    summary:
      'Each customer gets their own session with persistent memory, so follow-ups like “What about minor repairs?” just work. The bot answers service areas, services, pricing, warranties and scheduling rules 24/7 from the knowledge base — and can be customized with a client’s brand, FAQs, policies, booking, CRM, lead qualification and human handoff.',
    image: 'rag-production-telegram',
    alt: 'Telegram chats with the Summit Air Assistant answering service area, services and scheduling questions',
  },
  {
    id: 'sales-ai-agent',
    title: 'Sales Team AI Agent',
    summary:
      'Human-in-the-loop AI agent that reads incoming lead emails, classifies them and routes each one through an approval workflow.',
    image: 'sales-ai-agent',
    alt: 'n8n workflow of the Sales Team AI Agent',
  },
  {
    id: 'ai-assistant',
    title: 'Personal AI Assistant',
    summary:
      'A Telegram assistant that manages email, calendar scheduling, contacts and voice transcription — a whole back office in chat.',
    image: 'ai-assistant',
    alt: 'n8n workflow of the Personal AI Assistant',
  },
  {
    id: 'rag-chatbot',
    title: 'RAG Pipeline & Chatbot',
    summary:
      'A document chatbot on a Pinecone vector store with embeddings and Google Drive ingestion, answering from the team’s own files.',
    image: 'rag-chatbot',
    alt: 'n8n workflow of the RAG chatbot pipeline',
  },
  {
    id: 'invoice-processing',
    title: 'Automated Invoice Processing',
    summary:
      'Invoices arrive by Telegram bot, GPT-4o OCR extracts the fields, and the entries land in SAP — no manual data entry.',
    image: 'invoice-processing',
    alt: 'n8n workflow of the invoice processing pipeline',
  },
]

export const integrations: Build[] = [
  {
    id: 'gmail-integration',
    title: 'Gmail Integration Workflow',
    summary: 'Email processing, filtering and routing in Make, with custom business logic deciding where every message goes.',
    image: 'gmail-integration',
    alt: 'Make.com Gmail integration scenario',
  },
  {
    id: 'asana-xero',
    title: 'Asana & Xero Integration',
    summary: 'An accounting firm’s project management connected to its financial system, so billing and tasks track in one place.',
    image: 'asana-xero',
    alt: 'Make.com scenario connecting Asana and Xero',
  },
  {
    id: 'ghl-automation',
    title: 'GoHighLevel Automation',
    summary: 'CRM management, lead nurturing, appointment scheduling and client communication, all running on GHL workflows and webhooks.',
    image: 'ghl-automation',
    alt: 'GoHighLevel automation workflow for the AA Health Insurance pipeline',
  },
]

export const leadManagement: Build[] = [
  {
    id: 'leads-enrichment',
    title: 'Automated Leads Enrichment',
    summary: 'Apollo API enrichment with path-based routing for high and low priority leads, Slack notifications and drafted emails.',
    image: 'leads-enrichment',
    alt: 'Zapier workflow for automated lead enrichment',
  },
  {
    id: 'leads-action',
    title: 'Leads Action Automation',
    summary: 'Task-based lead management: folders created, email sequences started and status-based routing through Asana and Gmail.',
    image: 'leads-action',
    alt: 'Zapier workflow for task-based lead management',
  },
  {
    id: 'google-maps-scraper',
    title: 'Google Maps Scraper',
    summary: 'A Sheets-triggered Apify scrape with email finding and loop-based batch processing, built for steady lead generation.',
    image: 'google-maps-scraper',
    alt: 'n8n workflow for the Google Maps scraper',
  },
]

export const contentCreation: Build[] = [
  {
    id: 'content-repurposing',
    title: 'AI Content Repurposing',
    summary: 'Files from Google Drive become transcriptions, blog posts and social posts across platforms, automatically.',
    image: 'content-repurposing',
    alt: 'Zapier workflow for AI content repurposing',
  },
  {
    id: 'social-media',
    title: 'AI Social Media Content',
    summary: 'AI quote graphics generated and scheduled to Facebook, from idea to posted without a hand touching it.',
    image: 'social-media',
    alt: 'n8n workflow for AI social media content creation',
  },
  {
    id: 'youtube-shorts',
    title: 'YouTube Shorts Automation',
    summary: 'An ASMR Shorts channel run by agents: ideas, scripts and uploads on a schedule.',
    image: 'youtube-shorts',
    alt: 'n8n workflow for YouTube Shorts automation',
  },
  {
    id: 'viral-videos',
    title: 'AI Viral Video Generator',
    summary: 'NanoBanana images become VEO3 clips, distributed to every platform through Blotato.',
    image: 'viral-videos',
    alt: 'n8n workflow for the AI viral video generator',
  },
]

/* ---------- Platforms, running workflows, and what I keep alive ---------- */

export type ListedBuild = { name: string; summary: string; stack?: string }

export const platforms: ListedBuild[] = [
  { name: 'n8n', summary: 'Self-hosted flexibility: complex logic, custom API calls and AI agents.', stack: 'APIs · Expressions · AI agents' },
  { name: 'Make.com', summary: 'Visual scenario building for data-heavy, multi-step integrations.', stack: 'Scenarios · Routers · Iterators' },
  { name: 'Zapier', summary: 'Fast app-to-app automations when a workflow needs to ship quickly.', stack: 'Zaps · Paths · AI steps' },
]

export const supportWorkflows: { id: string; name: string; summary: string; image: string; alt: string }[] = [
  {
    id: 'wf-urgent',
    name: 'Urgent Stage',
    summary: 'Flags a ticket as urgent in the pipeline, tags it and notifies the support team instantly.',
    image: 'support-wf-062351',
    alt: 'GoHighLevel workflow for the Urgent stage',
  },
  {
    id: 'wf-in-progress',
    name: 'In Progress',
    summary: 'Moves a ticket into active work and removes it from the waiting automation.',
    image: 'support-wf-062506',
    alt: 'GoHighLevel workflow for the In Progress stage',
  },
  {
    id: 'wf-awaiting-reply',
    name: 'Awaiting Reply',
    summary: 'Sends the customer an additional-info request, waits three days, then reminds — and can close for no response.',
    image: 'support-wf-062554',
    alt: 'GoHighLevel workflow for the Awaiting Reply stage',
  },
  {
    id: 'wf-resolved',
    name: 'Resolved & Reopen',
    summary: 'Sends the resolved email, sets the reopen-window tag and updates the opportunity.',
    image: 'support-wf-062635',
    alt: 'GoHighLevel workflow for the Resolved stage',
  },
  {
    id: 'wf-replies',
    name: 'Customer Reply Handling',
    summary: 'A reply by SMS or email locates the ticket, moves it back to In Progress and notifies the team.',
    image: 'support-wf-062749',
    alt: 'GoHighLevel workflow for customer reply handling',
  },
  {
    id: 'wf-followup',
    name: 'Follow-up Window',
    summary: 'Waits out the seven-day reopen window, then clears the tag so closed tickets stay closed.',
    image: 'support-wf-062826',
    alt: 'GoHighLevel workflow for the reopen follow-up window',
  },
]

export const maintained: ListedBuild[] = [
  { name: 'Email & reports', summary: 'Scheduled email digests and report dashboards that go out on time, every time.', stack: 'n8n · Gmail · Dashboards' },
  { name: 'CRM updates', summary: 'Records stay current as deals, contacts and tickets move through the pipeline.', stack: 'GoHighLevel · Airtable · Sheets' },
  { name: 'Scheduling', summary: 'Appointments booked, reminded and rescheduled without anyone touching a calendar.', stack: 'Google Calendar · GHL · SMS' },
  { name: 'Documentation', summary: 'Every workflow documented and handed over, so the team can run it without me.', stack: 'Docs · Handover · Training' },
]

/** Number of builds across every Work chapter, shown as the sidebar badge. Derived, never typed in. */
export const workCount =
  2 +
  countBuilds(aiAutomations) +
  countBuilds(integrations) +
  leadManagement.length +
  contentCreation.length +
  platforms.length +
  supportWorkflows.length +
  maintained.length
